/** Compile forge.motion.v1 → token-colored CSS. Same sheet in preview and suite. */

import { inferMotionMotif, motifWantsGlyphs } from "./motion-motif";
import {
  hasDiscreteGraphic,
  motifParticleLayer,
  type MotionLayer,
  type MotionSpec,
} from "./motion-spec";

export function cssEscapeIdent(id: string): string {
  return String(id || "fx")
    .replace(/[^a-zA-Z0-9_-]/g, "-")
    .replace(/^-+/, "x")
    .slice(0, 48);
}

function token(color: string | undefined): string {
  switch (color) {
    case "secondary":
      return "var(--pf-cyan, #22d3ee)";
    case "magenta":
      return "var(--pf-magenta, #e879f9)";
    case "text":
      return "var(--pf-text, #f3e8ff)";
    case "glow":
      return "var(--pf-purple-glow, #c4b5fd)";
    default:
      return "var(--pf-purple, #a78bfa)";
  }
}

function layerCss(sel: string, layer: MotionLayer, i: number): string {
  const n = i + 1;
  if (layer.type === "none") return "";
  if (layer.type === "wallpaper") {
    const fit = layer.fit === "contain" ? "contain" : "cover";
    const drift = layer.drift
      ? `animation: sfm-drift ${8 + n}s ease-in-out infinite alternate;`
      : "";
    return `${sel}>.sf-motion-wall-${n}{position:absolute;inset:-6%;background-image:var(--sf-wallpaper,none);background-size:${fit};background-position:center;opacity:${layer.opacity};pointer-events:none;z-index:0;${drift}}`;
  }
  if (layer.type === "orbs") {
    const c = token(layer.color);
    return `${sel}>.sf-motion-orb-${n}{position:absolute;border-radius:50%;filter:blur(2px);pointer-events:none;z-index:1;opacity:${layer.opacity};animation:sfm-drift ${7 + n}s ease-in-out infinite alternate}
${sel}>.sf-motion-orb-${n}[data-k="a"]{width:42%;height:42%;left:8%;top:12%;background:radial-gradient(circle,color-mix(in srgb,${c} 55%,transparent),transparent 70%)}
${sel}>.sf-motion-orb-${n}[data-k="b"]{width:38%;height:38%;right:6%;bottom:10%;background:radial-gradient(circle,color-mix(in srgb,var(--pf-cyan,#22d3ee) 50%,transparent),transparent 70%);animation-duration:${9 + n}s;animation-direction:alternate-reverse}
${sel}>.sf-motion-orb-${n}[data-k="c"]{width:28%;height:28%;left:40%;top:6%;background:radial-gradient(circle,color-mix(in srgb,var(--pf-magenta,#e879f9) 40%,transparent),transparent 70%);animation-duration:${6 + n}s}`;
  }
  if (layer.type === "wash") {
    const a = token(layer.colors[0]);
    const b = token(layer.colors[1] || "secondary");
    if (layer.kind === "aurora") {
      return `${sel}>.sf-motion-band-${n}{position:absolute;left:-20%;width:140%;height:38%;filter:blur(18px);opacity:.75;pointer-events:none;z-index:1;animation:sfm-aurora ${6 + n}s ease-in-out infinite alternate}
${sel}>.sf-motion-band-${n}[data-k="1"]{top:10%;background:linear-gradient(90deg,transparent,color-mix(in srgb,${b} 55%,transparent),color-mix(in srgb,${a} 45%,transparent),transparent)}
${sel}>.sf-motion-band-${n}[data-k="2"]{top:42%;background:linear-gradient(90deg,transparent,color-mix(in srgb,var(--pf-magenta,#e879f9) 40%,transparent),color-mix(in srgb,${b} 35%,transparent),transparent);animation-duration:${8 + n}s;animation-direction:alternate-reverse}`;
    }
    if (layer.kind === "ribbons") {
      return `${sel}>.sf-motion-ribbon-${n}{position:absolute;inset:-28%;pointer-events:none;z-index:1;opacity:.85;background:repeating-linear-gradient(-32deg,transparent,transparent 22px,color-mix(in srgb,${a} 32%,transparent) 22px,color-mix(in srgb,${b} 26%,transparent) 36px);animation:sfm-ribbon 8s linear infinite}`;
    }
    if (layer.kind === "fog") {
      return `${sel}>.sf-motion-fog-${n}{position:absolute;inset:-25%;pointer-events:none;z-index:1;filter:blur(28px);opacity:.7;background:radial-gradient(ellipse at 30% 70%,color-mix(in srgb,${a} 35%,transparent),transparent 55%),radial-gradient(ellipse at 70% 40%,color-mix(in srgb,${b} 30%,transparent),transparent 50%);animation:sfm-fog ${10 + n}s ease-in-out infinite alternate}`;
    }
    if (layer.kind === "pulse") {
      return `${sel}>.sf-motion-pulse-${n}{position:absolute;inset:10%;pointer-events:none;z-index:1;border-radius:50%;background:radial-gradient(circle,color-mix(in srgb,${a} 40%,transparent),transparent 70%);animation:sfm-pulse ${2.4 + n * 0.4}s ease-in-out infinite}`;
    }
    return `${sel}>.sf-motion-mesh-${n}{position:absolute;inset:-18%;pointer-events:none;z-index:1;opacity:.9;background:linear-gradient(125deg,color-mix(in srgb,${a} 45%,transparent),color-mix(in srgb,${b} 40%,transparent),color-mix(in srgb,var(--pf-magenta,#e879f9) 32%,transparent),color-mix(in srgb,${a} 30%,transparent));background-size:200% 200%;animation:sfm-mesh 6s ease-in-out infinite}`;
  }
  if (layer.type === "grid") {
    const c = token(layer.color);
    const scan = layer.scanline
      ? `${sel}>.sf-motion-scan-${n}{position:absolute;left:0;right:0;height:16%;pointer-events:none;z-index:2;background:linear-gradient(transparent,color-mix(in srgb,var(--pf-cyan,#22d3ee) 35%,transparent),transparent);animation:sfm-scan 2.8s linear infinite}`
      : "";
    return `${sel}>.sf-motion-floor-${n}{position:absolute;left:-10%;right:-10%;bottom:-6%;height:68%;pointer-events:none;z-index:1;opacity:.7;background-image:linear-gradient(color-mix(in srgb,${c} 40%,transparent) 1px,transparent 1px),linear-gradient(90deg,color-mix(in srgb,${c} 40%,transparent) 1px,transparent 1px);background-size:32px 32px;transform:perspective(380px) rotateX(58deg);transform-origin:center bottom;animation:sfm-grid 4s linear infinite}${scan}`;
  }
  if (layer.type === "scan") {
    const c = token(layer.color);
    const axis = layer.axis === "y" ? "left" : "top";
    return `${sel}>.sf-motion-radar-${n}{position:absolute;${layer.axis === "y" ? "top:0;bottom:0;width:12%;" : "left:0;right:0;height:12%;"}pointer-events:none;z-index:2;background:linear-gradient(${layer.axis === "y" ? "90deg" : "180deg"},transparent,color-mix(in srgb,var(--pf-cyan,#22d3ee) 45%,transparent),color-mix(in srgb,${c} 55%,transparent),transparent);animation:sfm-scan 2.2s linear infinite}
${sel}>.sf-motion-radar-${n}{--sfm-scan-prop:${axis}}`;
  }
  if (layer.type === "particles") {
    const c = token(layer.color);
    if (layer.shape === "wave" || layer.direction === "left" || layer.direction === "right") {
      const name = layer.direction === "left" ? "sfm-stream-l" : "sfm-stream-r";
      return `${sel}>.sf-motion-wave-${n}{position:absolute;${layer.direction === "left" ? "right:-12%;" : "left:-12%;"}height:3px;border-radius:99px;pointer-events:none;z-index:2;background:linear-gradient(90deg,transparent,color-mix(in srgb,var(--pf-cyan,#22d3ee) 55%,#ecfeff),color-mix(in srgb,${c} 40%,#e0f2fe),transparent);box-shadow:0 0 12px color-mix(in srgb,var(--pf-cyan,#22d3ee) 70%,transparent);animation:${name} 2.8s linear infinite}`;
    }
    if (layer.shape === "flake") {
      return `${sel}>.sf-motion-flake-${n}{position:absolute;top:-8%;width:7px;height:7px;pointer-events:none;z-index:2;background:${c};clip-path:polygon(50% 0%,61% 35%,100% 35%,68% 57%,79% 91%,50% 70%,21% 91%,32% 57%,0 35%,39% 35%);box-shadow:0 0 8px ${c};animation:sfm-fall ${2.4 + n * 0.2}s linear infinite;opacity:.9}`;
    }
    if (layer.shape === "slash") {
      return `${sel}>.sf-motion-slash-${n}{position:absolute;top:-10%;width:2px;height:42px;pointer-events:none;z-index:2;background:linear-gradient(${c},transparent);transform:rotate(-28deg);box-shadow:0 0 10px ${c};animation:sfm-fall ${0.9 + n * 0.08}s linear infinite}`;
    }
    if (layer.shape === "ember") {
      return `${sel}>.sf-motion-glow-${n}{position:absolute;left:10%;right:10%;bottom:0;height:52%;pointer-events:none;z-index:1;background:radial-gradient(ellipse at 50% 100%,color-mix(in srgb,#f97316 65%,transparent),transparent 60%),radial-gradient(ellipse at 30% 100%,color-mix(in srgb,#fbbf24 45%,transparent),transparent 50%);animation:sfm-flame 3s ease-in-out infinite alternate}
${sel}>.sf-motion-ember-${n}{position:absolute;bottom:5%;border-radius:50%;width:5px;height:5px;background:#f97316;box-shadow:0 0 10px #f97316;pointer-events:none;z-index:2;animation:sfm-ember 2.2s ease-out infinite}`;
    }
    if (layer.shape === "hex") {
      return `${sel}>.sf-motion-hex-${n}{position:absolute;width:34px;height:38px;border:1.5px solid color-mix(in srgb,${c} 60%,transparent);clip-path:polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%);pointer-events:none;z-index:2;opacity:.7;animation:sfm-drift 10s ease-in-out infinite alternate}`;
    }
    if (layer.shape === "star") {
      return `${sel}>.sf-motion-star-${n}{position:absolute;border-radius:50%;background:#fff;box-shadow:0 0 6px #fff;pointer-events:none;z-index:2;animation:sfm-twinkle 2.4s ease-in-out infinite}`;
    }
    if (layer.shape === "streak" || layer.direction === "down") {
      return `${sel}>.sf-motion-drop-${n}{position:absolute;top:-15%;width:3px;border-radius:2px;pointer-events:none;z-index:2;background:linear-gradient(color-mix(in srgb,${c} 30%,#f8fafc),color-mix(in srgb,var(--pf-cyan,#22d3ee) 50%,transparent));box-shadow:0 0 10px color-mix(in srgb,${c} 80%,transparent);animation:sfm-fall 1.1s linear infinite}`;
    }
    return `${sel}>.sf-motion-spark-${n}{position:absolute;bottom:-5%;width:8px;height:8px;border-radius:50%;pointer-events:none;z-index:2;background:${c};box-shadow:0 0 8px ${c};animation:sfm-rise 4s linear infinite}`;
  }
  if (layer.type === "rays") {
    const c = token(layer.color);
    return `${sel}>.sf-motion-rays-${n}{position:absolute;inset:-20%;pointer-events:none;z-index:1;opacity:.55;background:repeating-conic-gradient(from 0deg at 50% 110%,transparent 0 12deg,color-mix(in srgb,${c} 35%,transparent) 12deg 16deg);animation:sfm-spin ${18 - layer.speed * 8}s linear infinite}`;
  }
  if (layer.type === "rings") {
    const c = token(layer.color);
    return `${sel}>.sf-motion-ring-${n}{position:absolute;left:50%;top:50%;border:2px solid color-mix(in srgb,${c} 55%,transparent);border-radius:50%;pointer-events:none;z-index:2;animation:sfm-ring ${2.6 + n * 0.4}s ease-out infinite}`;
  }
  return "";
}

function keyframes(): string {
  return `@keyframes sfm-drift{from{transform:translate(0,0) scale(1)}to{transform:translate(8%,-6%) scale(1.12)}}
@keyframes sfm-mesh{0%{background-position:0% 40%;transform:rotate(0) scale(1.05)}50%{background-position:100% 60%;transform:rotate(3deg) scale(1.15)}100%{background-position:0% 40%;transform:rotate(0) scale(1.05)}}
@keyframes sfm-aurora{from{transform:translateX(-10%) translateY(4%)}to{transform:translateX(10%) translateY(-4%)}}
@keyframes sfm-twinkle{0%,100%{opacity:.25;transform:scale(.8)}50%{opacity:1;transform:scale(1.25)}}
@keyframes sfm-rise{0%{transform:translateY(0) scale(.5);opacity:0}15%{opacity:1}100%{transform:translateY(-120%) scale(1);opacity:0}}
@keyframes sfm-grid{from{background-position:0 0}to{background-position:0 64px}}
@keyframes sfm-scan{from{top:-15%;left:-15%}to{top:105%;left:105%}}
@keyframes sfm-ribbon{from{transform:translate(0,0)}to{transform:translate(48px,28px)}}
@keyframes sfm-fall{0%{transform:translateY(0);opacity:0}10%{opacity:.85}100%{transform:translateY(120vh);opacity:.1}}
@keyframes sfm-stream-r{0%{transform:translateX(0);opacity:0}12%{opacity:.95}100%{transform:translateX(110vw);opacity:.12}}
@keyframes sfm-stream-l{0%{transform:translateX(0);opacity:0}12%{opacity:.95}100%{transform:translateX(-110vw);opacity:.12}}
@keyframes sfm-flame{from{transform:scaleY(.92) scaleX(1);opacity:.75}to{transform:scaleY(1.08) scaleX(1.04);opacity:1}}
@keyframes sfm-ember{0%{transform:translateY(0) scale(1);opacity:0}20%{opacity:1}100%{transform:translateY(-180px) scale(.3);opacity:0}}
@keyframes sfm-glyph-fall{0%{transform:translateY(0);opacity:0}10%{opacity:.9}100%{transform:translateY(120vh);opacity:.12}}
@keyframes sfm-glyph-stream{0%{transform:translateX(0);opacity:0}12%{opacity:.95}100%{transform:translateX(110vw);opacity:.12}}
@keyframes sfm-fog{from{transform:translate(-6%,4%) scale(1)}to{transform:translate(8%,-5%) scale(1.08)}}
@keyframes sfm-pulse{0%,100%{transform:scale(.7);opacity:.25}50%{transform:scale(1.15);opacity:.7}}
@keyframes sfm-spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}
@keyframes sfm-ring{0%{width:8%;height:8%;margin:-4% 0 0 -4%;opacity:.8}100%{width:90%;height:90%;margin:-45% 0 0 -45%;opacity:0}}
@media (prefers-reduced-motion:reduce){
  [data-bg-fx] .sf-motion-host *,.sf-stage .sf-motion-host *{animation-duration:.01ms !important;animation-iteration-count:1 !important}
}`;
}

function glyphCss(sel: string, spec: MotionSpec): string {
  const overlay = spec.glyphs;
  const motif = inferMotionMotif(spec.prompt, spec.name, spec.blurb, spec.id);
  if (!overlay && !motifWantsGlyphs(motif)) return "";
  const dir = overlay?.direction || (motif === "water" ? "right" : "down");
  const stream = dir === "left" || dir === "right";
  const color =
    motif === "water" || dir === "right"
      ? "color-mix(in srgb,var(--pf-cyan,#22d3ee) 55%,#ecfeff)"
      : "color-mix(in srgb,var(--pf-purple,#a78bfa) 45%,#f0fdf4)";
  const anim = stream ? "sfm-glyph-stream" : dir === "up" ? "sfm-rise" : "sfm-glyph-fall";
  return `${sel} .sf-fx-glyph{position:absolute;${stream ? "top:auto;left:-8%;" : "top:-12%;"}z-index:3;font-family:ui-monospace,Consolas,monospace;font-weight:700;color:${color};text-shadow:0 0 10px var(--pf-purple,#a78bfa);animation:${anim} 2.4s linear infinite;pointer-events:none}`;
}

export interface CompileOpts {
  /** Default `[data-bg-fx="{id}"]` for suite. Preview passes `.sf-stage[data-motion="…"]`. */
  selector?: string;
}

export function compileMotionCss(spec: MotionSpec, opts?: CompileOpts): string {
  const id = cssEscapeIdent(spec.id);
  const sel = opts?.selector || `[data-bg-fx="${id}"]`;
  const layers = spec.layers.filter((l) => l.type !== "none");
  const play = !hasDiscreteGraphic(layers) && layers.length
    ? [...layers, motifParticleLayer(`${spec.prompt} ${spec.name}`)]
    : layers;
  const parts = [
    `${sel} .sf-motion-host,${sel}.sf-motion-host{position:absolute;inset:0;overflow:hidden;pointer-events:none;z-index:0}`,
    ...play.map((l, i) => layerCss(sel, l, i)),
    glyphCss(sel, spec),
    keyframes(),
  ];
  return parts.filter(Boolean).join("\n");
}

/** Host nodes the compiled CSS expects (suite + preview). */
export function motionHostPlan(spec: MotionSpec): Array<{ className: string; attrs?: Record<string, string>; count?: number }> {
  const out: Array<{ className: string; attrs?: Record<string, string>; count?: number }> = [];
  spec.layers.forEach((layer, i) => {
    const n = i + 1;
    if (layer.type === "wallpaper") out.push({ className: `sf-motion-wall-${n}` });
    if (layer.type === "orbs") {
      out.push({ className: `sf-motion-orb-${n}`, attrs: { "data-k": "a" } });
      out.push({ className: `sf-motion-orb-${n}`, attrs: { "data-k": "b" } });
      out.push({ className: `sf-motion-orb-${n}`, attrs: { "data-k": "c" } });
    }
    if (layer.type === "wash" && layer.kind === "aurora") {
      out.push({ className: `sf-motion-band-${n}`, attrs: { "data-k": "1" } });
      out.push({ className: `sf-motion-band-${n}`, attrs: { "data-k": "2" } });
    }
    if (layer.type === "wash" && layer.kind === "mesh") out.push({ className: `sf-motion-mesh-${n}` });
    if (layer.type === "wash" && layer.kind === "ribbons") out.push({ className: `sf-motion-ribbon-${n}` });
    if (layer.type === "wash" && layer.kind === "fog") out.push({ className: `sf-motion-fog-${n}` });
    if (layer.type === "wash" && layer.kind === "pulse") out.push({ className: `sf-motion-pulse-${n}` });
    if (layer.type === "rays") out.push({ className: `sf-motion-rays-${n}` });
    if (layer.type === "rings") out.push({ className: `sf-motion-ring-${n}`, count: layer.count });
    if (layer.type === "grid") {
      out.push({ className: `sf-motion-floor-${n}` });
      if (layer.scanline) out.push({ className: `sf-motion-scan-${n}` });
    }
    if (layer.type === "scan") out.push({ className: `sf-motion-radar-${n}` });
    if (layer.type === "particles") {
      const count = Math.max(8, Math.min(40, layer.count));
      if (layer.shape === "wave" || ((layer.direction === "left" || layer.direction === "right") && layer.shape !== "flake" && layer.shape !== "slash" && layer.shape !== "dot")) {
        out.push({ className: `sf-motion-wave-${n}`, count });
      } else if (layer.shape === "flake") out.push({ className: `sf-motion-flake-${n}`, count });
      else if (layer.shape === "slash") out.push({ className: `sf-motion-slash-${n}`, count });
      else if (layer.shape === "ember") {
        out.push({ className: `sf-motion-glow-${n}` });
        out.push({ className: `sf-motion-ember-${n}`, count });
      } else if (layer.shape === "hex") out.push({ className: `sf-motion-hex-${n}`, count });
      else if (layer.shape === "star") out.push({ className: `sf-motion-star-${n}`, count });
      else if (layer.shape === "streak" || layer.direction === "down") {
        out.push({ className: `sf-motion-drop-${n}`, count });
      } else out.push({ className: `sf-motion-spark-${n}`, count });
    }
  });
  return out;
}
